import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  Grid,
  Snackbar,
  CssBaseline,
  FormLabel,
  Card,
  CardContent,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Divider,
  FormControl,
  Textarea,
  Select,
  Option,
  IconButton,
  Tooltip,
} from '@mui/joy';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import {
  listTrackingEventTypes,
  getTrackingEventType,
  createTrackingEventType,
  updateTrackingEventType,
  deleteTrackingEventType,
} from '../../main/API/trackingEventType';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import { masterApiErrorMessage } from '../../main/utils/masterApiError';
import { getPaginatedMeta } from '../../main/utils/pagination';
import { getTrackingEventTypeCatalogDisplayName } from '../../main/utils/trackingEventLabels';
import { resolveAppLanguage } from '../../../i18n/language';

function normalizeCodeInput(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '');
}

function buildPayload(form) {
  const sortRaw = form.sort_order;
  let sort_order = 0;
  if (sortRaw !== '' && sortRaw != null && !Number.isNaN(Number(sortRaw))) {
    sort_order = parseInt(String(sortRaw), 10);
  }
  return {
    code: normalizeCodeInput(form.code),
    name: String(form.name || '').trim(),
    name_ar: form.name_ar != null ? String(form.name_ar).trim() : '',
    sort_order,
  };
}

const emptyForm = () => ({
  code: '',
  name: '',
  name_ar: '',
  sort_order: 0,
});

export default function TrackingEventTypesMasterScreen() {
  const { t, i18n } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [data, setData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterCode, setFilterCode] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [ordering, setOrdering] = useState('sort_order');
  const [appliedOrdering, setAppliedOrdering] = useState('sort_order');

  const [failedNotification, setFailedNotification] = useState(false);
  const [successNotification, setSuccessNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [createErrors, setCreateErrors] = useState({});

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editErrors, setEditErrors] = useState({});

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const orderingOptions = useMemo(
    () => [
      { value: 'sort_order', label: t('settingsPages.trackingEventTypesMaster.orderSortLow') },
      { value: '-sort_order', label: t('settingsPages.trackingEventTypesMaster.orderSortHigh') },
      { value: 'code', label: t('settingsPages.trackingEventTypesMaster.orderCodeAz') },
      { value: '-code', label: t('settingsPages.trackingEventTypesMaster.orderCodeZa') },
      { value: 'name', label: t('settingsPages.trackingEventTypesMaster.orderNameAz') },
      { value: '-name', label: t('settingsPages.trackingEventTypesMaster.orderNameZa') },
      { value: 'id', label: t('settingsPages.trackingEventTypesMaster.orderIdAsc') },
      { value: '-id', label: t('settingsPages.trackingEventTypesMaster.orderIdDesc') },
    ],
    [t],
  );

  const load = useCallback(async () => {
    if (!hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST)) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        pagination: true,
        ordering: appliedOrdering,
      };
      if (appliedSearch.trim()) params.search = appliedSearch.trim();
      if (appliedCode.trim()) params.code = normalizeCodeInput(appliedCode.trim());

      const response = await listTrackingEventTypes(params);
      const meta = getPaginatedMeta(response.data, paginationModel.pageSize);
      setData(meta);
      setRows(meta.results);
    } catch (error) {
      console.error('Failed to load tracking event types:', error);
      setResponseMessage(masterApiErrorMessage(error, t) || t('settingsPages.trackingEventTypesMaster.loadFailed'));
      setFailedNotification(true);
      setRows([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, paginationModel.page, paginationModel.pageSize, appliedSearch, appliedCode, appliedOrdering, t]);

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize, appliedSearch, appliedCode, appliedOrdering]);

  const applyFilters = (e) => {
    e.preventDefault();
    setPaginationModel((p) => ({ ...p, page: 0 }));
    setAppliedSearch(search);
    setAppliedCode(filterCode);
    setAppliedOrdering(ordering);
  };

  const openDetail = async (row) => {
    if (!hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_VIEW)) {
      setDetailRow(row);
      setDetailOpen(true);
      return;
    }
    setDetailLoading(true);
    setDetailOpen(true);
    setDetailRow(null);
    try {
      const res = await getTrackingEventType(row.id);
      setDetailRow(res.data);
    } catch (error) {
      setDetailRow(row);
      setResponseMessage(masterApiErrorMessage(error, t) || t('settingsPages.trackingEventTypesMaster.detailLoadFailed'));
      setFailedNotification(true);
    } finally {
      setDetailLoading(false);
    }
  };

  const openCreate = () => {
    setCreateForm(emptyForm());
    setCreateErrors({});
    setCreateOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setEditForm({
      code: row.code || '',
      name: row.name || '',
      name_ar: row.name_ar != null ? row.name_ar : '',
      sort_order: row.sort_order != null ? row.sort_order : 0,
    });
    setEditErrors({});
    setEditOpen(true);
    setDetailOpen(false);
  };

  const validateForm = (payload) => {
    const errors = {};
    if (!payload.code || !/^[a-z0-9_]{1,50}$/.test(payload.code)) {
      errors.code = t('settingsPages.trackingEventTypesMaster.valCode');
    }
    if (!payload.name) errors.name = t('settingsPages.trackingEventTypesMaster.valNameRequired');
    return errors;
  };

  const submitCreate = async () => {
    if (!hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_CREATE)) return;
    const payload = buildPayload(createForm);
    const errs = validateForm(payload);
    setCreateErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await createTrackingEventType(payload);
      setResponseMessage(t('settingsPages.trackingEventTypesMaster.createdSuccess'));
      setSuccessNotification(true);
      setCreateOpen(false);
      load();
    } catch (error) {
      const d = error.response && error.response.data;
      if (error.response && error.response.status === 400 && d && typeof d === 'object' && !d.detail) {
        const fieldErr = {};
        Object.keys(d).forEach((k) => {
          const v = d[k];
          fieldErr[k] = Array.isArray(v) ? v[0] : String(v);
        });
        setCreateErrors(fieldErr);
      }
      const msg = masterApiErrorMessage(error, t) || t('settingsPages.trackingEventTypesMaster.createFailed');
      setResponseMessage(msg);
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const submitEdit = async () => {
    if (!editId) return;
    if (!hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_UPDATE)) return;
    const payload = buildPayload(editForm);
    const errs = validateForm(payload);
    setEditErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await updateTrackingEventType(editId, payload, true);
      setResponseMessage(t('settingsPages.trackingEventTypesMaster.updatedSuccess'));
      setSuccessNotification(true);
      setEditOpen(false);
      setEditId(null);
      load();
    } catch (error) {
      const d = error.response && error.response.data;
      if (error.response && error.response.status === 400 && d && typeof d === 'object' && !d.detail) {
        const fieldErr = {};
        Object.keys(d).forEach((k) => {
          const v = d[k];
          fieldErr[k] = Array.isArray(v) ? v[0] : String(v);
        });
        setEditErrors(fieldErr);
      }
      const msg = masterApiErrorMessage(error, t) || t('settingsPages.trackingEventTypesMaster.updateFailed');
      setResponseMessage(msg);
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (row) => {
    setDeleteError('');
    setDeleteTarget(row);
    setDeleteOpen(true);
  };

  const runDelete = async () => {
    if (!deleteTarget) return;
    if (!hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_DELETE)) return;
    setDeleteLoading(true);
    try {
      await deleteTrackingEventType(deleteTarget.id);
      setDeleteError('');
      setResponseMessage(t('settingsPages.trackingEventTypesMaster.deletedSuccess'));
      setSuccessNotification(true);
      setDeleteOpen(false);
      setDeleteTarget(null);
      setDetailOpen(false);
      load();
    } catch (error) {
      const msg = masterApiErrorMessage(error, t) || t('settingsPages.trackingEventTypesMaster.deleteFailed');
      setDeleteError(msg);
      setResponseMessage(msg);
      setFailedNotification(true);
    } finally {
      setDeleteLoading(false);
    }
  };

  const canCreate = hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_DELETE);

  const columns = useMemo(() => {
    const showArColumn = resolveAppLanguage(i18n.language) !== 'ar';
    const cols = [
      { field: 'id', headerName: t('settingsPages.masterScreens.colId'), width: 72 },
      { field: 'code', headerName: t('settingsPages.trackingEventTypesMaster.colCode'), flex: 1.2, minWidth: 180 },
      {
        field: 'name',
        headerName: t('settingsPages.trackingEventTypesMaster.colDisplayName'),
        flex: 1.5,
        minWidth: 200,
        renderCell: (params) => (
          <Typography level="body-sm">
            {getTrackingEventTypeCatalogDisplayName(params.row, i18n.language)}
          </Typography>
        ),
      },
    ];
    if (showArColumn) {
      cols.push({
        field: 'name_ar',
        headerName: t('settingsPages.trackingEventTypesMaster.colNameAr'),
        flex: 1.5,
        minWidth: 160,
        renderCell: (params) => (
          <Typography level="body-sm" dir="rtl">
            {params.value != null && String(params.value).trim() !== ''
              ? params.value
              : t('settingsPages.masterScreens.dash')}
          </Typography>
        ),
      });
    }
    cols.push(
      { field: 'sort_order', headerName: t('settingsPages.trackingEventTypesMaster.colSort'), width: 90 },
      {
        field: 'actions',
        headerName: t('settingsPages.masterScreens.colActions'),
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {canUpdate && (
              <Tooltip title={t('settingsPages.masterScreens.tooltipEdit')}>
                <IconButton size="sm" variant="soft" color="primary" onClick={() => openEdit(params.row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t('settingsPages.masterScreens.tooltipDelete')}>
                <IconButton size="sm" variant="soft" color="danger" onClick={() => confirmDelete(params.row)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    );
    return cols;
  }, [t, i18n.language, canUpdate, canDelete]);

  const rowCount = data && data.count != null ? data.count : rows.length;

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <Box sx={{ display: 'flex' }} m={3}>
        <Box width="100%">
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography component="nav" level="body-sm" sx={{ mb: 1 }}>
                <Link color="neutral" href="/settings/general">{t('nav.settings')}</Link>
                {' / '}
                <Typography component="span" sx={{ color: 'text.secondary' }}>
                  {t('settingsPages.trackingEventTypesMaster.breadcrumbMaster')}
                </Typography>
              </Typography>
              <Typography textAlign="start" level="h1" sx={{ fontSize: { xs: '1.5rem', sm: 'inherit' } }}>
                {t('settingsPages.trackingEventTypesMaster.pageTitle')}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'neutral.600', mt: 0.5, maxWidth: 720 }}>
                {t('settingsPages.trackingEventTypesMaster.introPrefix')}
                <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>event_type</Typography>
                {t('settingsPages.trackingEventTypesMaster.introSuffix')}
              </Typography>
            </Box>
            {canCreate && (
              <Button startDecorator={<AddIcon />} onClick={openCreate}>
                {t('settingsPages.trackingEventTypesMaster.btnNew')}
              </Button>
            )}
          </Stack>

          <PermissionGate
            permissions={[PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST]}
            fallback={
              <Typography sx={{ p: 2 }}>{t('settingsPages.trackingEventTypesMaster.noListPermission')}</Typography>
            }
          >
            <Box component="form" onSubmit={applyFilters} sx={{ mb: 2 }}>
              <Grid container spacing={1} alignItems="flex-end">
                <Grid xs={12} sm={6} md={3}>
                  <FormLabel>{t('settingsPages.trackingEventTypesMaster.searchLabel')}</FormLabel>
                  <Input
                    placeholder={t('settingsPages.masterScreens.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    startDecorator={<SearchIcon />}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={2}>
                  <FormLabel>{t('settingsPages.trackingEventTypesMaster.codeExactLabel')}</FormLabel>
                  <Input
                    placeholder={t('settingsPages.trackingEventTypesMaster.codeExactPlaceholder')}
                    value={filterCode}
                    onChange={(e) => setFilterCode(e.target.value)}
                    slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <FormLabel>{t('settingsPages.trackingEventTypesMaster.orderingLabel')}</FormLabel>
                  <Select
                    value={ordering}
                    onChange={(_, v) => setOrdering(v || 'sort_order')}
                  >
                    {orderingOptions.map((o) => (
                      <Option key={o.value} value={o.value}>{o.label}</Option>
                    ))}
                  </Select>
                </Grid>
                <Grid xs={12} sm={6} md={2}>
                  <Button type="submit">{t('actions.apply')}</Button>
                </Grid>
              </Grid>
            </Box>

            <Card variant="outlined" sx={{ borderRadius: '12px' }}>
              <CardContent sx={{ p: 0 }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  loading={loading}
                  getRowId={(r) => r.id}
                  paginationMode="server"
                  rowCount={rowCount}
                  pageSizeOptions={[25, 50, 100]}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  disableRowSelectionOnClick
                  onRowDoubleClick={(params) => openDetail(params.row)}
                  autoHeight
                  sx={{ border: 'none' }}
                />
              </CardContent>
            </Card>
            <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 1 }}>
              {t('settingsPages.trackingEventTypesMaster.footerHint')}
            </Typography>
          </PermissionGate>

          <Snackbar
            open={failedNotification}
            color="danger"
            variant="soft"
            onClose={() => setFailedNotification(false)}
            autoHideDuration={10000}
          >
            <Typography level="body-sm" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
              {t('settingsPages.masterScreens.snackbarErrorTitle')}
            </Typography>
            <Typography level="body-sm">{responseMessage}</Typography>
          </Snackbar>
          <Snackbar
            open={successNotification}
            color="success"
            variant="soft"
            onClose={() => setSuccessNotification(false)}
            autoHideDuration={4000}
          >
            {responseMessage}
          </Snackbar>
        </Box>
      </Box>

      <Modal open={detailOpen} onClose={() => setDetailOpen(false)}>
        <ModalDialog sx={{ maxWidth: 480 }}>
          <ModalClose />
          <Typography level="title-lg" sx={{ mb: 1 }}>
            {t('settingsPages.trackingEventTypesMaster.detailTitle')}
          </Typography>
          {detailLoading && <Typography level="body-sm">{t('settingsPages.masterScreens.loading')}</Typography>}
          {!detailLoading && detailRow && (
            <>
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Box><Typography level="body-xs" sx={{ color: 'neutral.600' }}>{t('settingsPages.trackingEventTypesMaster.labelId')}</Typography><Typography>{detailRow.id}</Typography></Box>
                <Box><Typography level="body-xs" sx={{ color: 'neutral.600' }}>{t('settingsPages.trackingEventTypesMaster.colCode')}</Typography><Typography sx={{ fontFamily: 'monospace' }}>{detailRow.code}</Typography></Box>
                <Divider />
                <Box><Typography level="body-xs" sx={{ color: 'neutral.600' }}>{t('settingsPages.trackingEventTypesMaster.colNameEn')}</Typography><Typography>{detailRow.name || t('settingsPages.masterScreens.dash')}</Typography></Box>
                <Box><Typography level="body-xs" sx={{ color: 'neutral.600' }}>{t('settingsPages.trackingEventTypesMaster.colNameAr')}</Typography><Typography dir="rtl">{detailRow.name_ar || t('settingsPages.masterScreens.dash')}</Typography></Box>
                <Box><Typography level="body-xs" sx={{ color: 'neutral.600' }}>{t('settingsPages.trackingEventTypesMaster.labelSortOrder')}</Typography><Typography>{detailRow.sort_order != null ? detailRow.sort_order : t('settingsPages.masterScreens.dash')}</Typography></Box>
              </Stack>
              <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
                {canUpdate && (
                  <Button onClick={() => openEdit(detailRow)}>{t('actions.edit')}</Button>
                )}
                {canDelete && (
                  <Button color="danger" variant="soft" onClick={() => confirmDelete(detailRow)}>
                    {t('actions.delete')}
                  </Button>
                )}
              </Stack>
            </>
          )}
        </ModalDialog>
      </Modal>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)}>
        <ModalDialog sx={{ maxWidth: 480, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="title-lg" sx={{ mb: 2 }}>{t('settingsPages.trackingEventTypesMaster.modalNewTitle')}</Typography>
          <Stack spacing={1.5}>
            <FormControl required error={!!createErrors.code}>
              <FormLabel>{t('settingsPages.trackingEventTypesMaster.colCode')}</FormLabel>
              <Input
                placeholder={t('settingsPages.trackingEventTypesMaster.codePlaceholder')}
                value={createForm.code}
                onChange={(e) => setCreateForm((p) => ({ ...p, code: e.target.value }))}
                onBlur={() => setCreateForm((p) => ({ ...p, code: normalizeCodeInput(p.code) }))}
                slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
              />
              {createErrors.code && (
                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>{createErrors.code}</Typography>
              )}
            </FormControl>
            <FormControl required error={!!createErrors.name}>
              <FormLabel>{t('settingsPages.masterScreens.labelNameEn')}</FormLabel>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
              />
              {createErrors.name && (
                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>{createErrors.name}</Typography>
              )}
            </FormControl>
            <FormControl error={!!createErrors.name_ar}>
              <FormLabel>{t('settingsPages.masterScreens.labelNameAr')}</FormLabel>
              <Textarea
                minRows={2}
                value={createForm.name_ar}
                onChange={(e) => setCreateForm((p) => ({ ...p, name_ar: e.target.value }))}
              />
            </FormControl>
            <FormControl error={!!createErrors.sort_order}>
              <FormLabel>{t('settingsPages.trackingEventTypesMaster.labelSortOrder')}</FormLabel>
              <Input
                type="number"
                value={createForm.sort_order}
                onChange={(e) => setCreateForm((p) => ({ ...p, sort_order: e.target.value }))}
              />
            </FormControl>
            <Stack direction="row" gap={1} justifyContent="flex-end">
              <Button variant="plain" color="neutral" onClick={() => setCreateOpen(false)}>{t('actions.cancel')}</Button>
              <Button loading={loading} onClick={submitCreate}>{t('settingsPages.masterScreens.btnCreate')}</Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)}>
        <ModalDialog sx={{ maxWidth: 480, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="title-lg" sx={{ mb: 2 }}>{t('settingsPages.trackingEventTypesMaster.modalEditTitle', { id: editId })}</Typography>
          <Stack spacing={1.5}>
            <FormControl required error={!!editErrors.code}>
              <FormLabel>{t('settingsPages.trackingEventTypesMaster.colCode')}</FormLabel>
              <Input
                value={editForm.code}
                onChange={(e) => setEditForm((p) => ({ ...p, code: e.target.value }))}
                onBlur={() => setEditForm((p) => ({ ...p, code: normalizeCodeInput(p.code) }))}
                slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
              />
              {editErrors.code && (
                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>{editErrors.code}</Typography>
              )}
            </FormControl>
            <FormControl required error={!!editErrors.name}>
              <FormLabel>{t('settingsPages.masterScreens.labelNameEn')}</FormLabel>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
              />
            </FormControl>
            <FormControl>
              <FormLabel>{t('settingsPages.masterScreens.labelNameAr')}</FormLabel>
              <Textarea
                minRows={2}
                value={editForm.name_ar}
                onChange={(e) => setEditForm((p) => ({ ...p, name_ar: e.target.value }))}
              />
            </FormControl>
            <FormControl>
              <FormLabel>{t('settingsPages.trackingEventTypesMaster.labelSortOrder')}</FormLabel>
              <Input
                type="number"
                value={editForm.sort_order}
                onChange={(e) => setEditForm((p) => ({ ...p, sort_order: e.target.value }))}
              />
            </FormControl>
            <Stack direction="row" gap={1} justifyContent="flex-end">
              <Button variant="plain" color="neutral" onClick={() => setEditOpen(false)}>{t('actions.cancel')}</Button>
              <Button loading={loading} onClick={submitEdit}>{t('actions.save')}</Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={deleteOpen} onClose={() => {
        if (deleteLoading) return;
        setDeleteOpen(false);
        setDeleteError('');
      }}
      >
        <ModalDialog variant="outlined" role="alertdialog">
          <Typography level="title-lg">{t('settingsPages.trackingEventTypesMaster.deleteTitle')}</Typography>
          <Typography level="body-sm" sx={{ mt: 1 }}>
            {deleteTarget && (
              <>
                <Typography component="span" sx={{ fontFamily: 'monospace' }}>{deleteTarget.code}</Typography>
                {' — '}
                {getTrackingEventTypeCatalogDisplayName(deleteTarget, i18n.language) || t('settingsPages.masterScreens.dash')}
              </>
            )}
          </Typography>
          {deleteError ? (
            <Typography level="body-sm" color="danger" sx={{ mt: 1.5, p: 1.5, borderRadius: 'sm', bgcolor: 'danger.softBg' }}>
              {deleteError}
            </Typography>
          ) : null}
          <Typography level="body-xs" sx={{ color: 'neutral.600', mt: 1 }}>
            {t('settingsPages.trackingEventTypesMaster.deleteHint')}
          </Typography>
          <Stack direction="row" gap={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
            <Button variant="plain" color="neutral" disabled={deleteLoading} onClick={() => { setDeleteOpen(false); setDeleteError(''); }}>{t('actions.cancel')}</Button>
            <Button color="danger" loading={deleteLoading} onClick={runDelete}>{t('actions.delete')}</Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </DirectionalMaterialThemeProvider>
  );
}
